import {
  Autocomplete,
  Box,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { AwsRdsEngine, Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import {
  PasswordManagement,
  getPasswordManagementPayload,
  defaultPasswordManagement,
} from '@/components/restore/passwordManagementComponent';
import { RdsInstanceClassSelect } from '@/components/selectors/rdsInstanceClassSelect';
import { RDSSubnetGroupSelect } from '@/components/selectors/rdsSubnetGroupSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { SecurityGroupSelect } from '@/components/selectors/securityGroupSelect';
import { BrowserGrid } from '@/components/shared/browserGrid';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreRDSState } from '../restoreRDSWizard';

const RESTORE_MODE = {
  INSTANCE: 'instance',
  DATABASES: 'databases',
} as const;

const NAMING_MODE = {
  CREATE_NEW: 'create_new',
  KEEP_ORIGINAL: 'keep_original',
} as const;

export const RDSConfiguration = (props: StepProps<RestoreRDSState>) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const {
    RdsRestoreWithoutEncryptionEnabled,
    RdsRestorePreserveOriginalMemberNames,
    EnableRDSRestorePasswordManagement,
  } = useFeatureFlags();

  const rdsPasswordValidation = getCloudFieldConfig(
    Provider.Aws,
    'rdsPassword',
    { required: true }
  ).validation;
  const [isRestoring, setIsRestoring] = useState(false);

  const { data: inventoryResourceBody } = dal.inventory.useResource(
    props.wizardState.resourceId
  );

  const restoreMode = props.wizardState.restoreMode ?? RESTORE_MODE.INSTANCE;

  // Load databases for selection
  const { data: dbServers, isLoading: isLoadingDbServers } =
    dal.db.useObjectTreeMultiple(
      props.wizardState.resourceId ? [props.wizardState.resourceId] : [],
      props.wizardState.snapshot.id
    );

  const databases = useMemo(
    () => dbServers?.[0]?.databases?.map((db) => db.name) || [],
    [dbServers]
  );

  // Compute guard conditions for restore-by-database feature
  const engine =
    inventoryResourceBody?.resource.resourceProperties?.awsRds?.engine;
  const isPostgres =
    engine === AwsRdsEngine.AWS_RDS_ENGINE_POSTGRES ||
    engine === AwsRdsEngine.AWS_RDS_ENGINE_AURORA_POSTGRESQL;
  const restoreByDatabaseEnabled =
    props.wizardState.resourceId &&
    props.wizardState.snapshot?.id &&
    isPostgres &&
    databases.length > 0;

  const isCluster =
    !!inventoryResourceBody?.resource?.resourceProperties?.awsRds?.clusterId;
  const showNamingMode = RdsRestorePreserveOriginalMemberNames && isCluster;
  const namingMode = props.wizardState.preserveInstanceNames
    ? NAMING_MODE.KEEP_ORIGINAL
    : NAMING_MODE.CREATE_NEW;

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      securityGroupIds: undefined,
      rdsInstanceClass: undefined,
      vpc: undefined,
      rdsSubnetGroupName: undefined,
      regionName: state.initialRegionName,
      passwordManagement: defaultPasswordManagement,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  const canGoNext = (): boolean => {
    const pm = props.wizardState.passwordManagement;
    const passwordValid =
      !pm?.enabled ||
      pm.mode === 'secrets_manager' ||
      (pm.mode === 'self_managed' &&
        rdsPasswordValidation.validateInput(pm.password));

    return (
      !isRestoring &&
      !!props.wizardState.regionName &&
      (!!props.wizardState.preserveInstanceNames ||
        !!props.wizardState.resourceName) &&
      !!props.wizardState.securityGroupIds?.length &&
      !!props.wizardState.rdsSubnetGroupName &&
      (!props.wizardState.encryption?.isEncrypted ||
        !!getEncryptionKey(props.wizardState.encryption)) &&
      passwordValid &&
      (props.wizardState.databaseFilter === undefined ||
        props.wizardState.databaseFilter.length > 0)
    );
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };

    const encryptionKeyId = props.wizardState.encryption?.isEncrypted
      ? getEncryptionKey(props.wizardState.encryption) || ''
      : '';

    const passwordPayload = getPasswordManagementPayload(
      props.wizardState.passwordManagement
    );

    return dal.restore
      .database(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          awsRds: {
            tags,
            encryptionKeyId,
            restoredName: props.wizardState.resourceName || '',
            restoreRegion: props.wizardState.regionName || '',
            securityGroups: props.wizardState.securityGroupIds,
            dbInstanceClass: props.wizardState.rdsInstanceClass,
            subnetGroup: props.wizardState.rdsSubnetGroupName,
            databaseFilter: props.wizardState.databaseFilter,
            preserveInstanceNames: props.wizardState.preserveInstanceNames,
            ...(EnableRDSRestorePasswordManagement ? passwordPayload : {}),
          },
        },
      })
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.resourceName || inventoryResourceBody?.resource.resourceName || ''}`
        )
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore RDS: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] mb-[24px] mt-[44px]'>
        <BrowserGrid gap='36px'>
          {restoreByDatabaseEnabled && (
            <FormControl size='small'>
              <FormLabel>Restore granularity</FormLabel>
              <ToggleButtonGroup
                value={restoreMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    if (newMode === RESTORE_MODE.INSTANCE) {
                      props.setWizardState((state) => ({
                        ...state,
                        restoreMode: newMode,
                        databaseFilter: undefined,
                      }));
                    } else if (newMode === RESTORE_MODE.DATABASES) {
                      props.setWizardState((state) => ({
                        ...state,
                        restoreMode: newMode,
                        databaseFilter: state.databaseFilter ?? [],
                      }));
                    }
                  }
                }}
                size='small'
                fullWidth
              >
                <ToggleButton value={RESTORE_MODE.INSTANCE}>
                  All databases
                </ToggleButton>
                <ToggleButton value={RESTORE_MODE.DATABASES}>
                  Selected databases
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          )}

          {restoreByDatabaseEnabled &&
            restoreMode === RESTORE_MODE.DATABASES && (
              <FormControl size='small'>
                <FormLabel>Databases to restore</FormLabel>
                <Autocomplete
                  multiple
                  size='small'
                  options={databases}
                  loading={isLoadingDbServers}
                  value={props.wizardState.databaseFilter || []}
                  onChange={(event, newValue) =>
                    props.setWizardState((state) => ({
                      ...state,
                      databaseFilter: newValue,
                    }))
                  }
                  renderOption={(optionProps, option, { selected }) => {
                    const { key, ...otherProps } = optionProps;
                    return (
                      <li key={key} {...otherProps}>
                        <Checkbox
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        props.wizardState.databaseFilter?.length
                          ? ''
                          : 'Select databases'
                      }
                      size='small'
                    />
                  )}
                  disabled={isLoadingDbServers || databases.length === 0}
                />
              </FormControl>
            )}

          {showNamingMode && (
            <FormControl size='small'>
              <FormLabel>Cluster naming</FormLabel>
              <ToggleButtonGroup
                value={namingMode}
                exclusive
                onChange={(_event, newMode) => {
                  if (newMode !== null) {
                    const keepOriginal = newMode === NAMING_MODE.KEEP_ORIGINAL;
                    props.setWizardState((state) => ({
                      ...state,
                      preserveInstanceNames: keepOriginal,
                      resourceName: '',
                    }));
                  }
                }}
                size='small'
                fullWidth
              >
                <ToggleButton value={NAMING_MODE.CREATE_NEW}>
                  Create new
                </ToggleButton>
                <ToggleButton value={NAMING_MODE.KEEP_ORIGINAL}>
                  Keep original
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          )}

          {!props.wizardState.preserveInstanceNames && (
            <FormControl size='small'>
              <FormLabel>
                {isCluster ? 'Database cluster name' : 'Database instance name'}
              </FormLabel>
              <TextField
                data-testid='databaseNameTextField'
                size='small'
                value={props.wizardState.resourceName || ''}
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    resourceName: event.target.value,
                  }))
                }
              />
            </FormControl>
          )}

          <RestoreRegionSelect
            isRequired
            cloudProvider={Provider.Aws}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(region) =>
              props.setWizardState((state) => ({
                ...state,
                regionName: region,
                rdsSubnetGroupName: undefined,
                securityGroupIds: undefined,
                vpc: undefined,
                rdsInstanceClass: undefined,
              }))
            }
          />
          <RDSSubnetGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            rdsSubnetGroupName={props.wizardState.rdsSubnetGroupName}
            onChange={(rdsSubnetGroupName, vpc) =>
              props.setWizardState((state) => ({
                ...state,
                rdsSubnetGroupName,
                vpc,
                securityGroupIds: undefined,
              }))
            }
          />
          <SecurityGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            securityGroupIds={props.wizardState.securityGroupIds}
            vpc={props.wizardState.vpc || ''}
            isSubnetIdMandatory={false}
            isOptionalField={false}
            onChange={(securityGroupIds) =>
              props.setWizardState((state) => ({
                ...state,
                securityGroupIds,
              }))
            }
          />
          <RdsInstanceClassSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            engine={
              inventoryResourceBody?.resource.resourceProperties?.awsRds?.engine
            }
            engineVersion={
              inventoryResourceBody?.resource.resourceProperties?.awsRds
                ?.engineVersion
            }
            rdsInstanceClass={props.wizardState.rdsInstanceClass}
            isCluster={isCluster}
            onChange={(rdsInstanceClass) =>
              props.setWizardState((state) => ({
                ...state,
                rdsInstanceClass,
              }))
            }
          />
        </BrowserGrid>
      </Box>
      <Divider />
      {props.wizardState.regionName && (
        <>
          <CardContent className='p-0'>
            <EncryptionComponent
              canBeTurnedOff={RdsRestoreWithoutEncryptionEnabled}
              encryption={props.wizardState.encryption}
              accountId={props.wizardState.restoreAccount!.id}
              regionName={props.wizardState.regionName}
              onChange={(encryption) =>
                props.setWizardState((state) => ({
                  ...state,
                  encryption,
                }))
              }
            />
          </CardContent>
          <Divider />
        </>
      )}
      {props.wizardState.regionName && EnableRDSRestorePasswordManagement && (
        <>
          <CardContent className='p-0'>
            <PasswordManagement
              passwordManagement={props.wizardState.passwordManagement}
              accountId={props.wizardState.restoreAccount!.id}
              regionName={props.wizardState.regionName}
              restoreAccount={props.wizardState.restoreAccount}
              onChange={(passwordManagement) =>
                props.setWizardState((state) => ({
                  ...state,
                  passwordManagement,
                }))
              }
            />
          </CardContent>
          <Divider />
        </>
      )}
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
